import * as React from 'react'
import { rem } from 'polished'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import Group from 'components/utils/Group'
import { useAppSelector } from 'hooks/reduxHooks'
import { mediaHoverable } from 'lib/theme/mediaQueries'
import Badge from 'LuxKit/Badge'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import LuxPlusLogo from 'luxPlus/components/Logos/LuxPlusLogo'
import LoyaltyProductLogo from 'loyalty/components/LoyaltyProductLogo'
import { LOYALTY_LANDING_PAGE } from 'loyalty/constants/loyaltyRoutes'
import { getCanRedeemLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'

/**
 * ClickableListItem applies a neutral hover background; restate product surfaces on :hover so rows stay static (no extra hover treatment).
 */
const LuxPlusListItem = styled(ClickableListItem)`
  min-height: ${rem(50)};
  background-color: ${props => props.theme.palette.product.luxPlus.background};
  color: ${props => props.theme.palette.product.luxPlus.contrast};
  border-radius: ${props => props.theme.borderRadius.L};

  &:not(:disabled) {
    ${mediaHoverable} {
      &:hover {
        background-color: ${props => props.theme.palette.product.luxPlus.background};
      }
    }
  }
`

const LoyaltyListItem = styled(ClickableListItem)`
  min-height: ${rem(50)};
  background: ${props => props.theme.palette.product.loyalty.bronze.gradientSecondaryBackground};
  border-radius: ${props => props.theme.borderRadius.L};

  &:not(:disabled) {
    ${mediaHoverable} {
      &:hover {
        background: ${props => props.theme.palette.product.loyalty.bronze.gradientSecondaryBackground};
      }
    }
  }
`

interface Props {
  isNew?: boolean
  variant: 'luxPlus' | 'loyalty'
  onClick?: React.MouseEventHandler<HTMLButtonElement>
}

function NavBarHamburgerMenuLoyaltyItem(props: Props) {
  const { variant, isNew, onClick } = props
  const intl = useIntl()

  const isLuxPlusMember = useAppSelector(state => getCanRedeemLuxPlusBenefits(state))

  const luxPlusJoinText = intl.formatMessage({
    id: 'riiInfd4OuMemvufgOfd',
    defaultMessage: 'Join our VIP Travel Club',
    description: 'Text shown for non-members to join Lux Plus for the membership',
  })

  const luxPlusMemberText = intl.formatMessage({
    id: 'fasgrSyV_71UXvEDjPuU',
    defaultMessage: 'VIP Travel Club',
    description: 'Text shown for Lux Plus members in the navigation bar',
  })

  const loyaltyText = intl.formatMessage({
    id: 'wC6SYCmvrT8Eou0DuUtt',
    defaultMessage: 'Earn points with',
    description: 'Text shown for loyalty program in the loyalty program section',
  })

  const newBadgeText = intl.formatMessage({
    id: 'CiWoHQLd_PEziL9FryAx',
    defaultMessage: 'New',
    description: 'Badge text indicating that a feature or item is newly available',
  })

  if (variant === 'luxPlus') {
    return (
      <LuxPlusListItem
        size="medium"
        onClick={onClick}
        to="/lux-plus"
        titleLineClamp={0}
        title={
          <Group direction="horizontal" verticalAlign="center" gap={16}>
            {!isLuxPlusMember ? luxPlusJoinText : luxPlusMemberText}
            <LuxPlusLogo colour="inherit" height={12} />
          </Group>
        }
        titleColour="lux-plus-contrast"
      />
    )
  }

  return (
    <LoyaltyListItem
      size="medium"
      onClick={onClick}
      to={`/${LOYALTY_LANDING_PAGE}`}
      titleLineClamp={0}
      endIcon={isNew ? <Badge mode="critical" kind="string">{newBadgeText}</Badge> : undefined}
      title={
        <Group direction="horizontal" verticalAlign="center" gap={16}>
          {loyaltyText}
          <LoyaltyProductLogo height={19} />
        </Group>
      }
      titleColour="neutral-one"
    />
  )
}

export default NavBarHamburgerMenuLoyaltyItem
